import { Box, Button, Chip, Typography } from '@mui/material';
import { SquarePenIcon, ZapIcon } from 'lucide-react';
import { Statute } from '../../types/Statute.type';
import EnhancementBadge from './EnhancementBadge';
import { ChargeEnhancement } from './EnhancementsDialog';

interface EnhancementsPreviewProps {
  caseCharges: Statute[];
  enhancementStatutes: Statute[];
  chargeEnhancements: ChargeEnhancement[];
  onOpen: () => void;
}

export default function EnhancementsPreview({
  caseCharges,
  enhancementStatutes,
  chargeEnhancements,
  onOpen,
}: EnhancementsPreviewProps) {
  const hasCase = enhancementStatutes.length > 0;
  const activeChargeGroups = chargeEnhancements.filter((e) => e.enhancements.length > 0);
  const hasCharge = activeChargeGroups.length > 0;

  return (
    <Box sx={{ padding: 2, width: 300 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
        <ZapIcon size={15} />
        <Typography
          variant='body2'
          sx={{
            fontWeight: 700,
          }}
        >
          Enhancements
        </Typography>
      </Box>
      {hasCase && (
        <Box sx={{ marginBottom: hasCharge ? 2.5 : 0 }}>
          <Typography
            variant='caption'
            sx={{ display: 'block', color: 'text.secondary', marginBottom: 1, fontWeight: 500 }}
          >
            Case enhancement
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {enhancementStatutes.map((s) => (
              <Chip key={s.id} label={s.display_name} size='small' />
            ))}
          </Box>
        </Box>
      )}
      {hasCharge && (
        <Box>
          <Typography
            variant='caption'
            sx={{ display: 'block', color: 'text.secondary', marginBottom: 1, fontWeight: 500 }}
          >
            Charge enhancement
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {activeChargeGroups.flatMap((group) => {
              const enh = group.enhancements[0];
              const assignedCharges = caseCharges.filter((c) => group.assignedChargeIds.includes(c.id));
              return assignedCharges.map((c) => (
                <Box
                  key={`${group.id}-${c.id}`}
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}
                >
                  <EnhancementBadge label={enh?.display_name ?? 'Enhancement'} />
                  <Chip label={c.display_name} size='small' />
                </Box>
              ));
            })}
          </Box>
        </Box>
      )}
      <Box sx={{ marginTop: 4 }}>
        <Button
          variant='outlined'
          size='small'
          color='accentBlue'
          startIcon={<SquarePenIcon size={14} />}
          onClick={onOpen}
          sx={{ fontWeight: 600, paddingX: '16px' }}
        >
          Edit
        </Button>
      </Box>
    </Box>
  );
}
